import { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Link,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { MarkdownMessage } from '@pv/common/assistants/MarkdownMessage';
import { MessageAttachments } from '@pv/common/assistants';
import {
  DataView,
  Disclosure,
  duration,
  humanize,
  LongText,
  RawJson,
  record,
  StatusBadge,
  timestamp,
} from './DataView';
import ToolResult, { resultSummary } from './ToolResult';
import type { AdminEntry, AdminTool } from './types';

export const failureMessage = (error: unknown) => {
  if (typeof error === 'string') return error;
  const message = record(error)?.message;
  return typeof message === 'string' ? message : undefined;
};

export function timelineItems(entries: AdminEntry[], audit: boolean) {
  const sorted = [
    ...new Map(entries.map((entry) => [entry.id, entry])).values(),
  ].sort((a, b) => a.sequence - b.sequence);
  const tools = new Map<string, AdminTool>();
  const anchors = new Map<string, AdminEntry>();
  const events = new Map<string, AdminEntry[]>();
  sorted.forEach((entry) => {
    if (!entry.tool) return;
    const id = entry.tool.id;
    tools.set(id, entry.tool);
    const anchor = anchors.get(id);
    if (!anchor || (anchor.kind !== 'tool_call' && entry.kind === 'tool_call'))
      anchors.set(id, entry);
    events.set(id, [...(events.get(id) || []), entry]);
  });
  return sorted
    .filter(
      (entry) => audit || !entry.tool || anchors.get(entry.tool.id) === entry
    )
    .map((entry) => ({
      entry,
      tool: entry.tool ? tools.get(entry.tool.id) : undefined,
      events: entry.tool ? events.get(entry.tool.id) || [] : [],
    }));
}

function ToolCard({
  entry,
  tool,
  events,
  audit,
  followsTool,
}: {
  entry: AdminEntry;
  tool: AdminTool;
  events: AdminEntry[];
  audit: boolean;
  followsTool: boolean;
}) {
  const { arguments: inputs, output, ...diagnostics } = tool;
  const error = failureMessage(tool.error);
  const failed = ['failed', 'timed_out', 'unavailable'].includes(tool.status);
  return (
    <Paper
      component="article"
      aria-label={`${humanize(tool.name)} ${audit ? entry.kind : 'execution'}`}
      variant="outlined"
      sx={{
        p: 1.5,
        mt: followsTool ? 1 : 2.5,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        <BuildOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2">{humanize(tool.name)}</Typography>
        <StatusBadge status={tool.status} />
        {tool.durationMs != null && (
          <Typography variant="caption" color="text.secondary">
            {duration(tool.durationMs)}
          </Typography>
        )}
        <Typography
          component="time"
          dateTime={entry.createdAt}
          variant="caption"
          color="text.secondary"
          title={entry.createdAt}
          sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
        >
          {timestamp(entry.createdAt)}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.5 }}
      >
        <Box component="code">{tool.name}</Box> · Tool {tool.id}
        {entry.runId ? ` · Run ${entry.runId}` : ''}
        {audit ? ` · ${entry.kind} · Entry ${entry.sequence}` : ''}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, overflowWrap: 'anywhere' }}
      >
        {resultSummary(tool)}
      </Typography>
      {(error || failed) && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <LongText
            text={
              error ||
              'Tool execution failed. Open diagnostics for recorded details.'
            }
          />
        </Alert>
      )}
      <Box sx={{ mt: 1 }}>
        <Disclosure label="View execution">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Inputs
          </Typography>
          <DataView value={inputs} />
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Result
          </Typography>
          <ToolResult tool={tool} />
          <Disclosure label="Diagnostics">
            <Typography variant="caption" color="text.secondary">
              Current recorded tool state; timeline timestamps identify the
              original events.
            </Typography>
            <DataView
              value={{
                ...diagnostics,
                timelineEvents: events.map(
                  ({ id, sequence, kind, createdAt, runId }) => ({
                    id,
                    sequence,
                    kind,
                    createdAt,
                    runId,
                  })
                ),
              }}
            />
          </Disclosure>
          <RawJson value={tool} />
        </Disclosure>
      </Box>
    </Paper>
  );
}

export default function AdminTimeline({
  entries,
  userEmail,
  userId,
  userName,
}: {
  entries: AdminEntry[];
  userEmail?: string;
  userId?: string;
  userName?: string;
}) {
  const initials = (userName || userEmail?.split('@')[0] || 'User')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  const [audit, setAudit] = useState(false);
  const items = useMemo(() => timelineItems(entries, audit), [entries, audit]);
  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Timeline</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={audit ? 'audit' : 'combined'}
          onChange={(_event, value) => {
            if (value) setAudit(value === 'audit');
          }}
          aria-label="Timeline view"
          sx={{
            '& .MuiToggleButton-root.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            },
          }}
        >
          <ToggleButton value="combined">Combined view</ToggleButton>
          <ToggleButton value="audit">Audit view</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {audit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Original entry order and timestamps. Tool details show the current
          recorded state, not a historical snapshot.
        </Alert>
      )}
      {!items.length && (
        <Typography color="text.secondary" sx={{ py: 3 }}>
          No conversation history recorded.
        </Typography>
      )}
      {items.map(({ entry, tool, events }, index) => {
        if (entry.message)
          return (
            <Paper
              component="article"
              variant="outlined"
              key={entry.id}
              sx={{
                p: { xs: 2, md: 3 },
                mt: index === 0 ? 0 : 2.5,
                bgcolor: 'background.paper',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: 1.5 }}
              >
                {entry.message.role === 'user' && (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.pink',
                      color: 'white',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {initials}
                  </Avatar>
                )}
                {entry.message.role === 'assistant' && (
                  <Avatar
                    variant="rounded"
                    src="/PV_logo_blue_32@2x.png"
                    alt="Perfect Venue"
                    sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                  />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {entry.message.role === 'user' ? (
                      userEmail && userId ? (
                        <Link
                          component={RouterLink}
                          to={`/admin/users/${userId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          {userEmail}
                        </Link>
                      ) : (
                        userEmail || 'User'
                      )
                    ) : entry.message.role === 'assistant' ? (
                      'Event Assistant'
                    ) : (
                      humanize(entry.message.role)
                    )}
                  </Typography>
                </Box>
                <Typography
                  component="time"
                  dateTime={entry.createdAt}
                  variant="caption"
                  color="text.secondary"
                  title={entry.createdAt}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {timestamp(entry.createdAt)}
                </Typography>
                {entry.message.status === 'incomplete' && (
                  <Chip size="small" color="warning" label="Incomplete" />
                )}
                {audit && (
                  <Typography variant="caption" color="text.secondary">
                    Entry {entry.sequence} · {entry.kind}
                  </Typography>
                )}
              </Stack>
              <Box sx={{ maxWidth: '85ch' }}>
                {entry.message.role === 'assistant' ? (
                  <MarkdownMessage
                    text={entry.message.content
                      .map((part) => part.text || '')
                      .join('\n\n')}
                  />
                ) : (
                  entry.message.content.map((part, index) => (
                    <LongText key={index} text={part.text || ''} />
                  ))
                )}
                <MessageAttachments message={entry.message} />
              </Box>
            </Paper>
          );
        if (tool)
          return (
            <ToolCard
              key={audit ? entry.id : `tool-${tool.id}`}
              entry={entry}
              tool={tool}
              events={events}
              audit={audit}
              followsTool={index > 0 && !!items[index - 1].tool}
            />
          );
        if (!audit && !entry.data?.message) return null;
        return (
          <Box key={entry.id} sx={{ py: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {humanize(entry.kind)} · {timestamp(entry.createdAt)}
              {audit ? ` · Entry ${entry.sequence}` : ''}
            </Typography>
            {entry.data?.message && <LongText text={entry.data.message} />}
            {audit && (
              <Disclosure label="Entry data">
                <DataView value={entry} />
              </Disclosure>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
